import { ReactElement, useEffect, useState, useContext } from 'react'
import MapGl, { Marker, useMap, GeolocateControl } from 'react-map-gl'
import styled, { ThemeContext } from 'styled-components'
import { device } from '../breakpoints'
import IcomoonReact from 'icomoon-react'
import iconSet from './icons.json'

const isMobile = window.innerWidth < 768
// Fly to the active marker
const FlyManager = ({ lat, lng }: { lat: number; lng: number }) => {
  const { current: map } = useMap()

  if (lat && lng && map) {
    map.flyTo({ center: [lng, lat] })
  }

  return <></>
}

const Map = ({
  handleMarkerClick,
  markers,
  handleMapLoaded,
  handleUserLocationFound,
  handleUserLocationError,
  activeMarker,
  locked,
  publisherDomain,
  locateMe,
}: {
  handleMarkerClick: (marker: any) => void
  markers: any[]
  handleMapLoaded: () => void
  handleUserLocationFound: (lat: number, lng: number) => void
  handleUserLocationError: (e: GeolocationPositionError) => void
  activeMarker: any
  locked: boolean
  publisherDomain: string | undefined
  locateMe: boolean
}): ReactElement => {
  const [geolocateControlRef, setGeolocateControlRef] = useState<any>(null)
  const themeContext = useContext(ThemeContext)

  const getColorForStyle = (style: string) => {
    const colorForStyle = {
      pop: '#E16D00',
      classique: '#378EBD',
      jazz: '#BA7FB7',
      pluridisciplinaire: '#479260',
    }
    if (Object.keys(colorForStyle).includes(style)) {
      return colorForStyle[style]
    } else {
      return themeContext.textColor
    }
  }

  // Get the user location
  useEffect(() => {
    if (locateMe && geolocateControlRef) {
      console.log('Trigger geolocateControl')
      geolocateControlRef.trigger()
    }
  }, [locateMe, geolocateControlRef])

  // Center and zoom the map according to the tenant and device
  let center = isMobile
    ? [6.5830064030215, 46.96128017136573]
    : [6.652325818326, 46.47206962682728]
  let zoom = isMobile ? 8 : 9

  if (publisherDomain === 'tdg') {
    center = isMobile
      ? [6.18072345739543, 46.59120509182557]
      : [6.4683048111211, 46.39067031102019]
  }

  const mapStyle =
    themeContext.colorMode === 'light'
      ? 'clx0hz6w700zy01po249y4u83'
      : 'clx0i0cka01me01r0hblgbrm2'

  const boundMargin = 0.2
  const bounds = [
    [5.5 - boundMargin, 45.4 - boundMargin], // sud ouest
    [8.2 + boundMargin, (isMobile ? 48.5 : 47.5) + boundMargin], // nord est
  ]

  return (
    <Container>
      <MapGl
        scrollZoom={isMobile ? true : !locked}
        dragPan={isMobile ? true : !locked}
        pitchWithRotate={false}
        dragRotate={false}
        onLoad={() => {
          handleMapLoaded()
        }}
        initialViewState={{
          longitude: center[0],
          latitude: center[1],
          zoom: zoom,
        }}
        onZoom={(e) => {
          console.log(e.viewState)
        }}
        minZoom={isMobile ? 7 : 8}
        maxZoom={14}
        maxBounds={bounds}
        style={{ width: '100%', height: '100%' }}
        mapStyle={`mapbox://styles/ta-interaktiv/${mapStyle}`}
        mapboxAccessToken='pk.eyJ1IjoidGEtaW50ZXJha3RpdiIsImEiOiJja292ZWk5ZnIwN2w0MndwZWQ0Nmpqcm1iIn0.d1OaviBwQQGO1_roYeaimQ'
      >
        <GeolocateControl
          ref={setGeolocateControlRef}
          position='bottom-right'
          onGeolocate={(res) => {
            handleUserLocationFound(res.coords.latitude, res.coords.longitude)
          }}
          onError={(e: GeolocationPositionError) => {
            handleUserLocationError(e)
          }}
        />
        {activeMarker && (
          <FlyManager
            lat={activeMarker.latitude}
            lng={activeMarker.longitude}
          />
        )}
        {markers.map((marker) => (
          <Marker
            key={marker.markerId}
            longitude={marker.longitude}
            latitude={marker.latitude}
            anchor='center'
            style={{
              zIndex: marker.markerId === activeMarker?.markerId ? 1 : 0,
            }}
          >
            <MarkerContent
              $active={marker.markerId === activeMarker?.markerId}
              onClick={() => handleMarkerClick(marker)}
            >
              <IcomoonReact
                iconSet={iconSet}
                icon='violon'
                size={20}
                color={
                  marker.markerId === activeMarker?.markerId
                    ? '#ffffff'
                    : getColorForStyle(marker.main_style)
                }
              ></IcomoonReact>
            </MarkerContent>
          </Marker>
        ))}
      </MapGl>
    </Container>
  )
}

export default Map

const Container = styled.div`
  position: relative;
  background-color: #f5f6f7;

  @media ${device.mobileS}, ${device.mobileM}, ${device.mobileL} {
    width: 100%;
    margin-left: 0%;
    height: 100%;
  }

  @media ${device.tablet}, ${device.laptop} {
    width: 100%;
    margin-left: 0%;
    height: 100vh;
  }
`

const MarkerContent = styled.div<{ $active: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ $active, theme }) =>
    $active
      ? theme.mainColor
      : `rgba(
    ${theme.colorMode === 'light' ? '255, 255, 255' : '18,18,18'},
    0.5
  );`};
  width: 28px;
  height: 28px;
  border-radius: 50%;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
  cursor: pointer;
`
